import type { ConnectionStatusType, Studio } from "@/types";
import { getStudioLocationCityCountry } from "@/lib/studio-display";

export type StudioBookingSourceKey = "eversports" | "mindbody" | "bsport" | "none" | "other";

export type StudioDataAppliedFilters = {
  instagram: ConnectionStatusType[];
  whatsapp: ConnectionStatusType[];
  bookingPlatform: ConnectionStatusType[];
  bookingSources: StudioBookingSourceKey[];
  locations: string[];
};

export const EMPTY_STUDIO_DATA_FILTERS: StudioDataAppliedFilters = {
  instagram: [],
  whatsapp: [],
  bookingPlatform: [],
  bookingSources: [],
  locations: [],
};

const BOOKING_SOURCE_LABELS: Record<StudioBookingSourceKey, string> = {
  eversports: "Eversports",
  mindbody: "Mindbody",
  bsport: "Bsport",
  none: "None",
  other: "Other",
};

export function studioBookingSourceLabel(key: StudioBookingSourceKey): string {
  return BOOKING_SOURCE_LABELS[key];
}

export function normalizeStudioBookingSource(raw: string | null | undefined): StudioBookingSourceKey {
  if (raw == null || !String(raw).trim()) return "none";
  const x = String(raw).trim().toLowerCase();
  if (x.includes("eversport")) return "eversports";
  if (x.includes("mindbody")) return "mindbody";
  if (x.includes("bsport")) return "bsport";
  return "other";
}

const STATUS_LABELS: Record<ConnectionStatusType, string> = {
  connected: "Connected",
  error: "Error",
  not_connected: "Not connected",
};

export function studioConnectionStatusLabel(status: ConnectionStatusType): string {
  return STATUS_LABELS[status];
}

function channelPasses(studio: Studio, channel: "instagram" | "whatsapp" | "bookingPlatform", selected: ConnectionStatusType[]): boolean {
  if (selected.length === 0) return true;
  const st = studio.connections[channel].status;
  return selected.includes(st);
}

export function filterStudiosByDataFilters(studios: Studio[], f: StudioDataAppliedFilters): Studio[] {
  return studios.filter((s) => {
    if (!channelPasses(s, "instagram", f.instagram)) return false;
    if (!channelPasses(s, "whatsapp", f.whatsapp)) return false;
    if (!channelPasses(s, "bookingPlatform", f.bookingPlatform)) return false;
    if (f.bookingSources.length > 0) {
      const key = s.bookingSourceKey ?? "none";
      if (!f.bookingSources.includes(key)) return false;
    }
    if (f.locations.length > 0) {
      const loc = getStudioLocationCityCountry(s);
      if (!loc || !f.locations.includes(loc)) return false;
    }
    return true;
  });
}

export function activeStudioDataFilterCount(f: StudioDataAppliedFilters): number {
  return (
    f.instagram.length +
    f.whatsapp.length +
    f.bookingPlatform.length +
    f.bookingSources.length +
    f.locations.length
  );
}

export function cloneStudioDataFilters(f: StudioDataAppliedFilters): StudioDataAppliedFilters {
  return {
    instagram: [...f.instagram],
    whatsapp: [...f.whatsapp],
    bookingPlatform: [...f.bookingPlatform],
    bookingSources: [...f.bookingSources],
    locations: [...f.locations],
  };
}

/** Distinct location labels (city + country) for filter chips, sorted. */
export function uniqueStudioLocationOptions(studios: Studio[]): string[] {
  const set = new Set<string>();
  for (const s of studios) {
    const loc = getStudioLocationCityCountry(s);
    if (loc) set.add(loc);
  }
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}
